import React, { useState, useEffect, useRef, useCallback } from "react";
import { Film, Image, Play, Pause, Maximize2, Loader2, X, ArrowLeft, Clapperboard } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { getPublicVideoLabel } from "../../utils/projectRuntime";

const PAGE_SIZE = 20;

// 瀑布流卡片
const GalleryCard = React.memo(({ item, onOpen }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const isVideo = item.category === "video";

  const handleMouseEnter = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
      setPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setPlaying(false);
    }
  };

  return (
    <div
      className="group relative rounded-xl overflow-hidden cursor-pointer break-inside-avoid mb-3"
      style={{ backgroundColor: "var(--color-bg-secondary)" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onOpen(item)}
    >
      {/* 内容 */}
      {isVideo ? (
        <video
          ref={videoRef}
          src={item.url}
          poster={item.thumbnail || ""}
          className="w-full block"
          muted
          playsInline
          preload="none"
          loop
        />
      ) : (
        <img src={item.url} alt="" className="w-full block animate-in fade-in duration-300" loading="lazy" />
      )}

      {/* hover 遮罩 */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* 类型标记 */}
      {isVideo && (
        <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium" style={{ backgroundColor: "var(--overlay-medium)", color: "rgba(255,255,255,0.8)" }}>
          {playing ? <Pause size={9} /> : <Play size={9} fill="white" />}
          {item.duration && <span>{item.duration}</span>}
        </div>
      )}

      {/* 放大按钮 */}
      <button className="absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: "var(--overlay-light)", color: "white" }}>
        <Maximize2 size={12} />
      </button>

      {/* 底部信息 */}
      <div className="absolute bottom-0 left-0 right-0 px-2.5 pb-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {item.prompt && (
          <p className="text-2xs leading-relaxed line-clamp-2 mb-1.5" style={{ color: "rgba(255,255,255,0.8)" }}>
            {item.prompt}
          </p>
        )}
        <div className="flex items-center gap-1.5">
          {item.projectName && (
            <span className="text-2xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)" }}>
              {item.projectName}
            </span>
          )}
          {item.provider && (
            <span className="text-2xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "color-mix(in srgb, var(--color-purple-primary) 18%, transparent)", color: "var(--color-purple-primary)" }}>
              {getPublicVideoLabel(item.provider, item.provider)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

// Lightbox
const Lightbox = ({ item, onClose }) => {
  const videoRef = useRef(null);
  if (!item) return null;
  const isVideo = item.category === "video";

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center animate-in fade-in duration-150" style={{ backgroundColor: "var(--overlay-heavy)" }} onClick={onClose}>
      <button className="absolute top-4 right-4 p-2 rounded-full z-10" style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }} onClick={onClose}>
        <X size={20} />
      </button>
      <div className="max-w-[90vw] max-h-[90vh] animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video ref={videoRef} src={item.url} className="max-w-full max-h-[85vh] rounded-lg" controls autoPlay />
        ) : (
          <img src={item.url} alt="" className="max-w-full max-h-[85vh] rounded-lg object-contain" />
        )}
        {item.prompt && (
          <p className="text-xs mt-3 text-center leading-relaxed max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.5)" }}>
            {item.prompt}
          </p>
        )}
      </div>
    </div>
  );
};

// 主页面
export default function GalleryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("all"); // all | video | image
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null);
  const pageRef = useRef(1);
  const sentinelRef = useRef(null);
  const allCacheRef = useRef([]); // 缓存全量数据用于前端分页

  // 加载项目列表
  useEffect(() => {
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/list`);
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) { console.error("加载项目列表失败:", e); }
    })();
  }, []);

  // 加载资源（项目/筛选变化时重新加载全量，前端分页）
  useEffect(() => {
    if (projects.length === 0) return;

    const fetchAll = async () => {
      setLoading(true);
      pageRef.current = 1;
      allCacheRef.current = [];

      const targetProjects = selectedProject ? [selectedProject] : projects;
      const allItems = [];

      // 并发请求所有项目
      const promises = targetProjects.map(async (proj) => {
        const projectId = proj.id || proj;
        const payload = {
          projectId: parseInt(projectId),
          sortBy: "created_at",
          sortOrder: "desc",
          pageSize: 200,
        };
        if (filter === "video") payload.category = "video";
        if (filter === "image") payload.category = "image";

        try {
          const resources = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (Array.isArray(resources)) {
            return resources
              .filter(r => r.url && (r.category === "video" || r.category === "image"))
              .map(r => {
                let metadata = {};
                try { metadata = typeof r.metadata === "string" ? JSON.parse(r.metadata) : r.metadata || {}; } catch {}
                return {
                  id: `${projectId}-${r.id}`,
                  url: r.url,
                  thumbnail: r.thumbnail || r.url,
                  category: r.category,
                  prompt: metadata.prompt || r.prompt || "",
                  provider: metadata.provider || "",
                  duration: r.duration ? `${r.duration}s` : null,
                  projectId,
                  projectName: proj.name || "",
                  createdAt: r.createdAt,
                };
              });
          }
        } catch (e) { console.error(`加载项目 ${projectId} 资源失败:`, e); }
        return [];
      });

      const results = await Promise.all(promises);
      results.forEach(arr => allItems.push(...arr));

      // 按时间排序
      allItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      allCacheRef.current = allItems;

      // 取第一页
      setItems(allItems.slice(0, PAGE_SIZE));
      setHasMore(allItems.length > PAGE_SIZE);
      setLoading(false);
    };

    fetchAll();
  }, [projects, selectedProject, filter]);

  // 加载更多（从缓存切片）
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const start = pageRef.current * PAGE_SIZE;
    const next = allCacheRef.current.slice(start, start + PAGE_SIZE);
    setItems(prev => [...prev, ...next]);
    setHasMore(start + PAGE_SIZE < allCacheRef.current.length);
    pageRef.current += 1;
    setLoadingMore(false);
  }, [loadingMore, hasMore]);

  // 无限滚动
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const filterBtns = [
    { id: "all", label: "全部", icon: null },
    { id: "video", label: "视频", icon: <Film size={12} /> },
    { id: "image", label: "图片", icon: <Image size={12} /> },
  ];

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {/* 顶栏 */}
      <div className="shrink-0 px-6 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}><Clapperboard size={18} /> 作品展廊</h1>
        </div>

        {/* 类型筛选 */}
        <div className="flex items-center gap-1">
          {filterBtns.map(btn => (
            <button
              key={btn.id}
              onClick={() => setFilter(btn.id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: filter === btn.id ? "var(--color-accent-primary)" : "var(--color-bg-tertiary)",
                color: filter === btn.id ? "white" : "var(--color-text-muted)",
              }}
            >
              {btn.icon}
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* 项目筛选 */}
      {projects.length > 1 && (
        <div className="shrink-0 px-6 py-2.5 flex items-center gap-1.5 overflow-x-auto custom-scrollbar" style={{ borderBottom: "1px solid var(--color-border-secondary)" }}>
          <button
            onClick={() => setSelectedProject(null)}
            className="px-2.5 py-1 rounded-full text-2xs font-medium whitespace-nowrap transition-all"
            style={{
              backgroundColor: !selectedProject ? "color-mix(in srgb, var(--color-accent-primary) 12%, transparent)" : "var(--color-bg-tertiary)",
              color: !selectedProject ? "var(--color-accent-primary)" : "var(--color-text-muted)",
            }}
          >
            全部项目
          </button>
          {projects.map(proj => (
            <button
              key={proj.id}
              onClick={() => setSelectedProject(proj)}
              className="px-2.5 py-1 rounded-full text-2xs font-medium whitespace-nowrap transition-all"
              style={{
                backgroundColor: selectedProject?.id === proj.id ? "color-mix(in srgb, var(--color-accent-primary) 12%, transparent)" : "var(--color-bg-tertiary)",
                color: selectedProject?.id === proj.id ? "var(--color-accent-primary)" : "var(--color-text-muted)",
              }}
            >
              {proj.name}
            </button>
          ))}
        </div>
      )}

      {/* 瀑布流 */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Film size={40} style={{ color: "var(--color-text-muted)", opacity: 0.3 }} />
            <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>暂无作品</span>
          </div>
        ) : (
          <>
            <div style={{ columns: "3 280px", columnGap: 12 }}>
              {items.map(item => (
                <GalleryCard key={item.id} item={item} onOpen={setLightboxItem} />
              ))}
            </div>

            {/* 加载更多哨兵 */}
            <div ref={sentinelRef} className="flex items-center justify-center py-6">
              {loadingMore && <Loader2 size={20} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />}
              {!hasMore && items.length > 0 && <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>已加载全部</span>}
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
    </div>
  );
}
